import * as immutable from 'object-path-immutable'

import { buildCatalog, injectSellable, removeUnavailableProperties } from '@libs/server'
import {
  getLocalState,
  setPrefix,
  syncLocalTab
} from '@libs/client/hooks/useGlobalState/StateLocal'
import { store_setting_template, store_type } from '@libs/common/models/constant'

import { ParsedUrlQuery } from 'querystring'
import { deepCloneObject } from './utilities'
import { customConfig, hardcodePaygateContact, hardcodeSellpage } from './hardcode'
import { initRDT } from '@libs/client/hooks/useGlobalState/ReduxDevTool'
import { setGlobalState } from '@libs/client'

const defaultStore: S3Types.store = {
  setting: {
    template: store_setting_template.default,
    settings: {
      general: {
        id: '',
        description: '',
        domain: '',
        favicon: '',
        logo: {
          image: ''
        },
        meta: {
          description: '',
          dmca_site_verification: '',
          facebook_domain_verification: '',
          google_site_verification: '',
          image: '',
          title: ''
        },
        name: '',
        publisher_id: '',
        store_fee: {},
        title: '',
        type: store_type.SELLPAGE
      }
    }
  },
  pages: {}
}

export interface ServerState {
  domains?: S3Types.domains
  publisher?: S3Types.publisher
  store?: S3Types.store
  promotions?: {
    default: S3Types.promotions
    [slug: string]: S3Types.promotions
  }
  paygates?: { creditcard?: S3Types.paygate; paypalex?: S3Types.paygate }
  static?: {
    [static_slug: string]: S3Types.default_store_static_page
  }
  isPreview?: boolean
  analytics?: {
    pixel_ids: string[]
    tiktok_ids: string[]
    snapchat_ids: string[]
    pinterest_ids: string[]
    google_conversion_id: string
    google_conversion_label: string
  }
  customConfig?: any
  settingDefault?: S3Types.default_store
}

export function initializeClientState() {
  const INIT_STATE = {
    ...setPrefix(window?.__NEXT_DATA__?.props?.pageProps, ''),
    ...getLocalState()
  }
  setGlobalState(INIT_STATE, true)
  initRDT(INIT_STATE)
  // Set to false to avoid clearing state, this should be changed once we apply sync between tabs of browser.
  syncLocalTab((data: any) => setGlobalState(data))
}

export function initializeServerState(
  domain: string,
  subpath: string,
  pathname: string,
  query?: ParsedUrlQuery
) {
  const INIT_STATE = setPrefix(
    buildServerState({
      domain,
      subpath,
      pathname,
      query
    }),
    ''
  )
  setGlobalState(INIT_STATE, true)

  return INIT_STATE
}

function buildServerState(params: {
  domain: string
  subpath: string
  pathname: string
  query?: ParsedUrlQuery
}) {
  // const t = process.hrtime()
  const { domain, subpath, pathname, query } = params
  const serverState: ServerState = {}
  if (process._repository.default_store?.setting) {
    serverState.settingDefault = process._repository?.default_store
  }
  if (process._repository.domains?.[domain]) {
    serverState.domains = { [domain]: process._repository?.domains[domain] }
  }

  const store_id = serverState?.domains?.[domain]?.store_id

  if (store_id) {
    let store = process._repository?.stores?.[store_id]

    if (store) store = deepCloneObject(store)

    if (store && store.pages) {
      serverState.analytics = getAnalytics(store.pages, subpath)
      if (store.setting?.settings?.general?.type === store_type.SELLPAGE) {
        if (store.pages.setting) {
          injectSellable(store.pages.setting)
          removeUnavailableProperties(store.pages.setting.variants)
          hardcodeSellpage(domain, store.pages)
        }
      } else {
        Object.entries(store.pages).forEach(([page_path, page]) => {
          if (page) {
            injectSellable(page?.setting)
            removeUnavailableProperties(page?.setting?.variants)
            hardcodeSellpage(domain, page)
            if (store?.pages) store.pages[page_path] = page
          }
        })
        store.catalog = buildCatalog(store)
        // injectSoldoutSellpage(store)
        Object.keys(store.pages).map((page_path) => {
          if (page_path === subpath) {
            const page = store?.pages?.[subpath]
            if (page && store?.pages) {
              store.pages[subpath] = page
            }
          } else if (store?.pages) {
            store.pages = immutable.set(store?.pages || {}, `${page_path}.setting`, {})
            store.pages = immutable.set(store?.pages || {}, `${page_path}.reviews`, undefined)
          }
        })
      }

      serverState.promotions = getPromotions(store)
    }

    serverState.store = store

    const publisher =
      process._repository.publishers?.[store?.setting?.settings?.general?.publisher_id ?? '1']
    if (publisher) {
      serverState.paygates = paymentGatewaysHardcoded(domain, getPaymentGateways(publisher))
    }

    serverState.customConfig = customConfig(domain, subpath)
  }

  const _static: {
    [static_slug: string]: S3Types.default_store_static_page
  } = {}

  if (pathname.startsWith('/static') && query?.static_slug) {
    if (process._repository?.default_store?.setting?.static?.[query.static_slug as string]) {
      _static[query.static_slug as string] =
        process._repository?.default_store?.setting?.static?.[query.static_slug as string]
    }

    serverState.static = _static
  }

  serverState.isPreview = false

  // logger.info(`🏪 Store built in: ${getDurationInSeconds(t).toLocaleString()}s`)

  return serverState
}

const getPaymentGateways = (publisher: S3Types.publisher) => {
  const payments = publisher.payments
  const creditcardType = payments?.creditcard
  const paypalexType = payments?.paypalex
  const creditcardId =
    creditcardType === 'DEFAULT'
      ? process._repository?.default_paygate?.creditcard
      : creditcardType === 'SPECIFIC'
      ? payments?.creditcard_id
      : null
  const paypalexId =
    paypalexType === 'DEFAULT'
      ? process._repository?.default_paygate?.paypalex
      : paypalexType === 'SPECIFIC'
      ? payments?.paypalex_id
      : null
  return {
    ...(creditcardId && { creditcard: process._repository.paygates?.[creditcardId] }),
    ...(paypalexId && { paypalex: process._repository.paygates?.[paypalexId] })
  }
}

const getAnalytics = (pages?: S3Types.pages, subpath = '') => {
  if (pages?.setting) {
    return {
      pixel_ids: pages.setting.settings?.analytics?.pixel_ids ?? [],
      tiktok_ids: pages.setting.settings?.analytics?.tiktok_ids ?? [],
      snapchat_ids: pages.setting.settings?.analytics?.snapchat_ids ?? [],
      pinterest_ids: pages.setting.settings?.analytics?.pinterest_ids ?? [],
      google_conversion_id: pages.setting.settings?.analytics?.google_conversion_id ?? '',
      google_conversion_label: pages.setting.settings?.analytics?.google_conversion_label ?? ''
    }
  } else {
    if (pages) {
      if (pages?.[subpath]) {
        return {
          pixel_ids: pages?.[subpath].setting?.settings?.analytics?.pixel_ids ?? [],
          tiktok_ids: pages?.[subpath].setting?.settings?.analytics?.tiktok_ids ?? [],
          snapchat_ids: pages?.[subpath].setting?.settings?.analytics?.snapchat_ids ?? [],
          pinterest_ids: pages?.[subpath].setting?.settings?.analytics?.pinterest_ids ?? [],
          google_conversion_id:
            pages?.[subpath].setting?.settings?.analytics?.google_conversion_id ?? '',
          google_conversion_label:
            pages?.[subpath].setting?.settings?.analytics?.google_conversion_label ?? ''
        }
      }
    }
    return {
      pixel_ids: [],
      tiktok_ids: [],
      snapchat_ids: [],
      pinterest_ids: [],
      google_conversion_id: '',
      google_conversion_label: ''
    }
  }
}

const getPromotions = (store: S3Types.store): ServerState['promotions'] => {
  const promotionMapping = (promotions: S3Types.promotions): S3Types.promotions => {
    return Object.fromEntries(
      Object.entries(promotions).map(([key, value]) => [
        key,
        {
          code: value.code,
          value: value.value,
          type: value.type
        }
      ])
    )
  }

  return {
    default: promotionMapping(process._repository.default_store?.promotions ?? {}),
    ...Object.fromEntries(
      Object.entries(store.pages ?? {}).map(([slug, page]) => [
        slug,
        promotionMapping(page.promotions ?? {})
      ])
    )
  }
}

const paymentGatewaysHardcoded = (domain: string, paygates: ServerState['paygates']) => {
  const paygateContact = hardcodePaygateContact(domain)
  return {
    ...(paygates?.creditcard && {
      creditcard: {
        ...paygates?.creditcard,
        contact: { ...paygates?.creditcard.contact, ...paygateContact }
      }
    }),
    ...(paygates?.paypalex && {
      paypalex: {
        ...paygates?.paypalex,
        contact: { ...paygates?.paypalex.contact, ...paygateContact }
      }
    })
  }
}

export async function initializePreviewServerState(pathname: string, query?: ParsedUrlQuery) {
  const INIT_STATE = await buildPreviewServerState({ pathname, query })
  setGlobalState(INIT_STATE, true)

  return INIT_STATE
}

async function buildPreviewServerState(params: { pathname: string; query?: ParsedUrlQuery }) {
  // const t = process.hrtime()
  const { pathname, query } = params
  // const previewId = params.query?.page?.toString()
  const serverState: ServerState = {}

  // const { preview } = getPreviewService()

  // if (previewId) {
  //   try {
  //     const page = await preview(previewId)
  //     if (page && page.setting?.settings?.general?.id) {
  //       const store: S3Types.store = {
  //         ...defaultStore,
  //         pages: {
  //           [page.setting.settings.general.id]: page
  //         }
  //       }
  //       serverState.store = store
  //     }
  //   } catch (err) {
  //     console.error(err)
  //   }
  // }

  serverState.store = defaultStore

  const _static: {
    [static_slug: string]: S3Types.default_store_static_page
  } = {}
  if (pathname.startsWith('/static') && query?.static_slug) {
    if (process._repository?.default_store?.setting?.static?.[query.static_slug as string]) {
      _static[query.static_slug as string] =
        process._repository?.default_store?.setting?.static?.[query.static_slug as string]
    }

    serverState.static = _static
  }

  serverState.isPreview = true

  // logger.info(`🏪 Store built in: ${getDurationInSeconds(t).toLocaleString()}s`)

  return serverState
}
